/**
 * Shared header nav data + outbound link constants. Mirrors the corporate
 * marketing site (new_wp / providecars.co.jp, English chrome) so the portal
 * carries the same global navigation: Stock, Auctions, Countries, Pricing,
 * About, Blog, the "Auction Sign Up" pill and the same footer.
 *
 * Every target lives on the corporate site, the Stock site or provideauctions
 * as an absolute URL; the Stock site is the one this deploy pairs with
 * (VITE_STOCK_FRONTEND_URL).
 */
import type { NavItem } from './DesktopMegaItem';
import { getStockFrontendBase } from '../../lib/featureFlags';

/** True for absolute http(s) URLs, which must render as a real anchor (new tab)
 *  rather than going through react-router's <Link>. */
export function isExternalHref(href: string): boolean {
  return /^https?:\/\//.test(href);
}

/** Corporate marketing site (apex, .co.jp — canonical per the site SSOT). */
export const SITE_HREF = 'https://providecars.co.jp';
export const AUCTION_HREF = 'https://www.provideauctions.com/cars/';
/** cars-for-sale Stock site (external) — the header "Stock" entry links here. */
export const STOCK_HREF = `${getStockFrontendBase()}/`;
/** Stock sign-in — the top bar "Sign In" menu's Stock entry. */
export const STOCK_LOGIN_HREF = `${getStockFrontendBase()}/login`;
/** Primary CTA ("Auction Sign Up"), as on the corporate header. */
export const AUCTION_SIGNUP_HREF = 'https://www.provideauctions.com/join-new.asp';
export const SIGNIN_HREF = 'https://www.provideauctions.com/cars/signin.asp';
export const BLOG_HREF = `${SITE_HREF}/blog`;
export const CONTACT_HREF = `${SITE_HREF}/contact-us`;

/**
 * Primary nav, mirrored from the corporate header (new_wp LocalizedChrome).
 * Labels match the corporate English chrome strings exactly.
 */
export const DEFAULT_NAV: NavItem[] = [
  { title: 'Stock', path: STOCK_HREF },
  {
    title: 'Auctions',
    path: `${SITE_HREF}/about-auction`,
    children: [
      { title: 'USS Auction Guide', path: `${SITE_HREF}/about-auction/about-uss-auction` },
      { title: 'TAA Auction Guide', path: `${SITE_HREF}/about-auction/about-taa-auction` },
      { title: 'CAA Auction Guide', path: `${SITE_HREF}/about-auction/caa-auction` },
      {
        title: 'Auction Days & Venues',
        path: `${SITE_HREF}/about-auction/japanese-car-auction-days`,
      },
      {
        title: 'Auction Grading System',
        path: `${SITE_HREF}/about-auction/japanese-auction-houses-use-a-grading-system`,
      },
      {
        title: 'Other Auction Networks',
        path: `${SITE_HREF}/about-auction/other-auction-networks`,
      },
    ],
  },
  {
    title: 'Countries',
    path: `${SITE_HREF}/localized-page`,
    children: [
      { title: 'Australia', path: `${SITE_HREF}/au` },
      { title: 'Cyprus', path: `${SITE_HREF}/cy` },
      { title: 'Germany', path: `${SITE_HREF}/de` },
      { title: 'Ireland', path: `${SITE_HREF}/ie` },
      { title: 'Kenya', path: `${SITE_HREF}/ke` },
      { title: 'Netherlands', path: `${SITE_HREF}/nl` },
      { title: 'New Zealand', path: `${SITE_HREF}/nz` },
      { title: 'Poland', path: `${SITE_HREF}/pl` },
      { title: 'Sri Lanka', path: `${SITE_HREF}/lk` },
      { title: 'UK', path: `${SITE_HREF}/uk` },
    ],
  },
  {
    title: 'Pricing',
    path: `${SITE_HREF}/about-auction/detailed-provide-cars-import-process`,
    children: [
      { title: 'Commission & Service Fees', path: `${SITE_HREF}/bank-info/service-and-commission` },
      { title: 'FOB vs CIF', path: `${SITE_HREF}/bank-info/fob-cif` },
      { title: 'Payment & Bank Info', path: `${SITE_HREF}/bank-info` },
    ],
  },
  {
    title: 'About',
    path: `${SITE_HREF}/about-provide-cars`,
    children: [{ title: 'JUMVEA', path: `${SITE_HREF}/about-provide-cars/jumvea` }],
  },
];
